import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, Monitor, Smartphone, Tablet,
  Eye, Layout, Type, Image, RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

// Pages that can be edited with their current default values
const EDITABLE_PAGES = [
  { 
    id: 'home', 
    name: 'Homepage', 
    path: '/',
    icon: Layout,
    fields: [
      { key: 'hero_title', label: 'Hero Title', type: 'text', default: 'Escola de Samba Casa Samba - Afro-Brasilian Cultural School' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', default: 'Experience the authentic rhythms of Brasil. Curated by Mestre Curtis Pierre, preserving 38 years of cultural legacy through Capoeira and Brasilian percussion.' },
      { key: 'hero_button', label: 'Hero Button Text', type: 'text', default: 'Browse Music' },
    ]
  },
  { 
    id: 'music', 
    name: 'Music Library', 
    path: '/music',
    icon: Type,
    fields: [
      { key: 'music_title', label: 'Page Title', type: 'text', default: 'Music Library' },
      { key: 'music_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Discover authentic Brasilian rhythms' },
    ]
  },
  { 
    id: 'videos', 
    name: 'Video Store', 
    path: '/videos',
    icon: Image,
    fields: [
      { key: 'video_title', label: 'Page Title', type: 'text', default: 'Video Store' },
      { key: 'video_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Watch and learn from the masters' },
    ]
  },
  { 
    id: 'courses', 
    name: 'Courses', 
    path: '/courses',
    icon: Layout,
    fields: [
      { key: 'courses_title', label: 'Page Title', type: 'text', default: 'Escola de Samba Casa Samba Academy' },
      { key: 'courses_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Master the art of Afro-Brasilian drumming, dance, and culture' },
    ]
  },
  { 
    id: 'membership', 
    name: 'Membership', 
    path: '/membership',
    icon: Layout,
    fields: [
      { key: 'membership_title', label: 'Page Title', type: 'text', default: 'Membership' },
      { key: 'membership_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Join our community and unlock exclusive content' },
    ]
  },
];

export const AdminLiveEditor = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const iframeRef = useRef(null);
  
  const [activePage, setActivePage] = useState(pageId || 'home');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewSize, setPreviewSize] = useState('desktop');
  const [iframeKey, setIframeKey] = useState(0);

  const currentPage = EDITABLE_PAGES.find(p => p.id === activePage) || EDITABLE_PAGES[0];

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadContent();
  }, [activePage]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`/site-content/section/${activePage}`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    
    // Try to update the iframe preview in real-time
    updatePreview(key, value);
  };

  const updatePreview = (key, value) => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Send message to iframe to update content
        iframe.contentWindow.postMessage({
          type: 'UPDATE_CONTENT',
          key: key,
          value: value
        }, '*');
      }
    } catch (e) {
      // Cross-origin restrictions may prevent this
      console.log('Preview update not available');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(content).map(([key, value]) => ({
        key,
        value,
        section: activePage
      }));

      const response = await adminFetch('/admin/site-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        toast.success('Changes saved! Refreshing preview...');
        setHasChanges(false);
        // Refresh the iframe to show saved changes
        setIframeKey(prev => prev + 1);
      } else {
        toast.error('Failed to save changes');
      }
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = () => {
    setIframeKey(prev => prev + 1);
  };

  const getPreviewWidth = () => {
    switch (previewSize) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-background rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold">Live Page Editor</h1>
              <p className="text-xs text-muted-foreground">Edit and see changes in real-time</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Preview Size Toggle */}
            <div className="flex items-center bg-background rounded-lg p-1">
              <button
                onClick={() => setPreviewSize('mobile')}
                className={`p-2 rounded ${previewSize === 'mobile' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                title="Mobile"
              >
                <Smartphone className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewSize('tablet')}
                className={`p-2 rounded ${previewSize === 'tablet' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                title="Tablet"
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewSize('desktop')}
                className={`p-2 rounded ${previewSize === 'desktop' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                title="Desktop"
              >
                <Monitor className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={refreshPreview}
              className="p-2 hover:bg-background rounded-lg text-muted-foreground hover:text-foreground"
              title="Refresh Preview"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all ${
                hasChanges 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Editor */}
        <div className="w-80 flex-shrink-0 bg-card border-r border-border flex flex-col overflow-hidden">
          {/* Page Selector */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-semibold text-muted-foreground mb-2 block">SELECT PAGE</label>
            <select
              value={activePage}
              onChange={(e) => {
                setActivePage(e.target.value);
                setHasChanges(false);
              }}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {EDITABLE_PAGES.map(page => (
                <option key={page.id} value={page.id}>{page.name}</option>
              ))}
            </select>
          </div>

          {/* Edit Fields */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              currentPage.fields.map(field => (
                <div key={field.key} className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={content[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      placeholder={`Enter ${field.label.toLowerCase()}...`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={content[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={`Enter ${field.label.toLowerCase()}...`}
                    />
                  )}
                </div>
              ))
            )}

            {hasChanges && (
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-primary">
                  You have unsaved changes. Click "Save Changes" to update the page.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Live Preview */}
        <div className="flex-1 bg-background p-4 flex flex-col items-center overflow-hidden">
          <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="w-4 h-4" />
            <span>Live Preview - {currentPage.name}</span>
            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded">
              {previewSize}
            </span>
          </div>
          
          <div 
            className="flex-1 w-full flex justify-center overflow-hidden"
            style={{ maxWidth: getPreviewWidth() }}
          >
            <div className={`
              w-full h-full bg-white rounded-lg shadow-2xl overflow-hidden border-4 border-gray-800
              ${previewSize === 'mobile' ? 'rounded-3xl' : ''}
            `}>
              <iframe
                ref={iframeRef}
                key={iframeKey}
                src={`${SITE_URL}${currentPage.path}`}
                className="w-full h-full"
                title="Page Preview"
                style={{ 
                  transform: previewSize === 'mobile' ? 'scale(1)' : 'scale(1)',
                  transformOrigin: 'top left'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLiveEditor;
